import React, { FC } from 'react';
import { useQuery } from '@apollo/client';
import { FocusScope } from 'react-aria';
import { ContentLoader } from '@saucelabs/sl-component-loader';
import { replace } from 'connected-react-router';

import { graphql, useFragment } from '../../graphql/__generated__';
import { DiffHeaderInfoFragmentDoc } from '../../graphql/__generated__/graphql';
import DiffView from './DiffView';
import { BuildFrag, filterDiffNotQueued } from '../VisualBuildDetailsPage';
import { DiffFrag } from './Diff';
import { useVisualDispatch, useVisualSelector } from '../../reduxHooks';
import { selectors } from '../../ducks/buildDetails';
import DiffHeader from './DiffHeader';
import OverlayCloseButton from './components/OverlayCloseButton';
import { urls } from '../../constants';
import { ContentErrorHandler } from '../common/pages/ContentErrorHandler';

const DiffViewOverlayBuildQuery = graphql(`
  query DiffViewOverlayBuildQuery($buildId: UUID!, $filter: DiffFilter!) {
    build(id: $buildId){
      ...BuildFrag
    }
  }
`);

const DiffViewOverlayDiffQuery = graphql(`
  query DiffViewOverlayDiffQuery($id: UUID!) {
    diff(id: $id){
      ...DiffHeaderInfo
      ...DiffFrag
    }
  }
`);

export const DiffViewOverlay: FC<DiffViewOverlayProps> = (props) => {
  const { buildId, diffId } = props;
  const buildResult = useQuery(DiffViewOverlayBuildQuery, {
    variables: {
      buildId,
      filter: filterDiffNotQueued,
    },
  });
  const diffResult = useQuery(DiffViewOverlayDiffQuery, {
    variables: {
      id: diffId,
    },
  });
  const { loading } = buildResult;
  const build = useFragment(BuildFrag, buildResult.data?.build);
  const diff = useFragment(DiffFrag, diffResult.data?.diff);
  const diffHeaderInfo = useFragment(DiffHeaderInfoFragmentDoc, diffResult.data?.diff);

  const { allDiffIds } = useVisualSelector(selectors.root);
  const currentIndex = diffId ? allDiffIds.indexOf(diffId) : -1;
  const maxIndex = allDiffIds.length - 1;
  const nextIndex = currentIndex + 1;
  const nextDiffId = allDiffIds[nextIndex > maxIndex ? 0 : nextIndex];
  const prevIndex = currentIndex - 1;
  const prevDiffId = allDiffIds[prevIndex < 0 ? allDiffIds.length - 1 : prevIndex];
  const dispatch = useVisualDispatch();

  const closeOverlay = () => {
    dispatch(replace({
      pathname: urls.getDiff(buildId),
      search: window.location.search,
    }));
  };
  const nextDiff = () => {
    if (nextDiffId) {
      dispatch(replace({
        pathname: urls.getDiff(buildId, nextDiffId),
        search: window.location.search,
      }));
    }
  };
  const prevDiff = () => {
    if (prevDiffId) {
      dispatch(replace({
        pathname: urls.getDiff(buildId, prevDiffId),
        search: window.location.search,
      }));
    }
  };

  return (
    <FocusScope autoFocus>
      <ContentLoader show={loading && !build}>
        <div className="fixed left-0 top-0 h-full w-full flex flex-col z-1000 bg-white">
          <div className="w-full flex flex-row flex-nowrap">
            <DiffHeader
              className="flex-1 min-w-0"
              diff={diffHeaderInfo}
              diffIds={allDiffIds}
              nextPage={nextDiff}
              prevPage={prevDiff}
              stepId={diffId}
            />
            <div className="ml-auto border-0 border-l border-b border-solid border-gray-200 relative z-20 flex-shrink-0">
              <div className="mx-2 my-2">
                <OverlayCloseButton onPress={closeOverlay} />
              </div>
            </div>
          </div>

          <ContentErrorHandler
            isNotFoundError={!diffResult.data && !diffResult.loading}
            notFoundPageProps={{
              description: "We couldn't find the requested snapshot",
            }}
          >
            {diff && build && (
              <DiffView
                build={build}
                diff={diff}
                nextId={nextDiffId}
                nextPage={nextDiff}
                prevId={prevDiffId}
              />
            )}
          </ContentErrorHandler>
        </div>
      </ContentLoader>
    </FocusScope>
  );
};

export interface DiffViewOverlayProps {
  buildId: string;
  diffId: string;
}
